import React, { useState } from 'react';
import {
  Button,
  Flex,
  FlexItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { PlayIcon, PowerOffIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useServerPower } from '../../../hooks/useServerPower';
import { useNotifications } from '../../../contexts/NotificationContext';
import { PowerAction, Server } from '../../../types/models/Server';

interface ServerPowerActionsProps {
  server: Server;
}

const getDisabledTooltip = (action: PowerAction, status: string | null): string => {
  const statusLower = status?.toLowerCase() ?? '';
  switch (action) {
    case 'on':
      return statusLower === 'active' ? 'Server is already powered on' : 'Server cannot be powered on in current state';
    case 'off':
      return statusLower === 'offline'
        ? 'Server is already powered off'
        : 'Server cannot be powered off in current state';
    case 'cycle':
      return statusLower === 'offline' ? 'Server is powered off' : 'Server cannot be power cycled in current state';
  }
};

const ServerPowerActions: React.FunctionComponent<ServerPowerActionsProps> = ({ server }) => {
  const { sendPowerAction, loading: powerLoading } = useServerPower();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [confirmAction, setConfirmAction] = useState<PowerAction | null>(null);
  const [activeAction, setActiveAction] = useState<PowerAction | null>(null);

  const statusLower = server.status?.toLowerCase() ?? '';
  const isPowerOnAllowed = statusLower === 'offline';
  const isPowerOffAllowed = statusLower === 'active';
  const isPowerCycleAllowed = statusLower === 'active';

  const handlePowerAction = async (action: PowerAction) => {
    if (!server.id) return;
    setActiveAction(action);
    try {
      const result = await sendPowerAction(server.id, action);
      if (result.result === 'accepted') {
        addNotification({
          title: 'Power action sent',
          srTitle: 'Success alert',
          description: `Power ${action} command sent to ${server.name}.`,
          variant: 'success',
        });
      } else {
        addNotification({
          title: 'Power action response',
          srTitle: 'Warning alert',
          description: `Power ${action} command returned: ${result.result}`,
          variant: 'warning',
        });
      }
      setDrawerOpen(true);
    } catch (err) {
      addNotification({
        title: 'Power action failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setActiveAction(null);
      setConfirmAction(null);
    }
  };

  const onPowerButtonClick = (action: PowerAction) => {
    if (action === 'off' || action === 'cycle') {
      setConfirmAction(action);
    } else {
      handlePowerAction(action);
    }
  };

  const renderPowerButton = (
    action: PowerAction,
    variant: 'primary' | 'danger' | 'warning',
    icon: React.ReactNode,
    label: string,
    isAllowed: boolean,
  ) => {
    const isStatusDisabled = !isAllowed;
    const button = (
      <Button
        variant={variant}
        icon={activeAction === action ? <Spinner size="sm" /> : icon}
        isDisabled={powerLoading}
        isAriaDisabled={!powerLoading && isStatusDisabled}
        onClick={() => onPowerButtonClick(action)}
      >
        {label}
      </Button>
    );

    if (isStatusDisabled && !powerLoading) {
      return <Tooltip content={getDisabledTooltip(action, server.status)}>{button}</Tooltip>;
    }
    return button;
  };

  return (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>{renderPowerButton('on', 'primary', <PlayIcon />, 'Power On', isPowerOnAllowed)}</FlexItem>
        <FlexItem>{renderPowerButton('off', 'danger', <PowerOffIcon />, 'Power Off', isPowerOffAllowed)}</FlexItem>
        <FlexItem>
          {renderPowerButton('cycle', 'warning', <SyncAltIcon />, 'Power Cycle', isPowerCycleAllowed)}
        </FlexItem>
      </Flex>

      {confirmAction && (
        <Modal
          variant={ModalVariant.small}
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          aria-label="Confirm power action"
        >
          <ModalHeader title={`Confirm power ${confirmAction}`} titleIconVariant="warning" />
          <ModalBody>
            Are you sure you want to power {confirmAction} <strong>{server.name}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button
              variant={confirmAction === 'off' ? 'danger' : 'warning'}
              onClick={() => handlePowerAction(confirmAction)}
              isLoading={powerLoading}
              isDisabled={powerLoading}
            >
              Confirm
            </Button>
            <Button variant="link" onClick={() => setConfirmAction(null)} isDisabled={powerLoading}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ServerPowerActions;
